import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'

import LogoIcon from '@/assets/images/logo-icon.svg?react'
import { Button, Trans } from '@/components'
import { PLATFORM_HOME_PATH } from '@/consts/routes'
import { useIsLoggedIn, useUserStore } from '@/stores/user'
import {
  markWelcomeSeen,
  shouldShowWelcomeForNewUser,
  shouldShowWelcomeForUser,
} from '@/utils/welcome'

const REDIRECT_DELAY_SECONDS = 5

export function WelcomePage() {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const isLoggedIn = useIsLoggedIn()
  const userId = useUserStore((state) => state.id)
  const createTime = useUserStore((state) => state.createTime)
  const lastLoginAt = useUserStore((state) => state.lastLoginAt)
  const [remainingSeconds, setRemainingSeconds] = useState(REDIRECT_DELAY_SECONDS)

  const isPreview = useMemo(() => {
    const searchParams = new URLSearchParams(location.search)
    return import.meta.env.DEV && searchParams.has('preview')
  }, [location.search])

  const shouldShowWelcome =
    isPreview ||
    shouldShowWelcomeForUser(userId) ||
    shouldShowWelcomeForNewUser(userId, createTime, lastLoginAt)

  const redirectToPlatform = useCallback(() => {
    if (!isPreview) {
      markWelcomeSeen(userId)
    }

    navigate(PLATFORM_HOME_PATH, { replace: true })
  }, [isPreview, navigate, userId])

  useEffect(() => {
    if (isPreview || !isLoggedIn || !shouldShowWelcome) {
      return
    }

    setRemainingSeconds(REDIRECT_DELAY_SECONDS)

    const intervalId = window.setInterval(() => {
      setRemainingSeconds((currentSeconds) => Math.max(currentSeconds - 1, 0))
    }, 1000)
    const timeoutId = window.setTimeout(redirectToPlatform, REDIRECT_DELAY_SECONDS * 1000)

    return () => {
      window.clearInterval(intervalId)
      window.clearTimeout(timeoutId)
    }
  }, [isPreview, isLoggedIn, redirectToPlatform, shouldShowWelcome])

  if (!isPreview && !isLoggedIn) {
    return <Navigate to="/login" replace />
  }

  if (!shouldShowWelcome) {
    return <Navigate to={PLATFORM_HOME_PATH} replace />
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-8 px-6 py-8 bg-[image:var(--image-app-background)] bg-cover bg-no-repeat bg-center">
      <div className="card bg-panel-white w-[480px] max-w-full flex flex-col items-center overflow-hidden shadow-[0px_64px_240px_0px_rgba(33,51,101,0.05),0px_20px_72px_0px_rgba(33,51,101,0.03),0px_8px_32px_0px_rgba(33,51,101,0.03),0px_4px_12px_0px_rgba(33,51,101,0.02)]">
        <div className="w-full py-2 flex flex-col items-center justify-center gap-4">
          <LogoIcon className="auth-logo-icon w-10 h-10" />

          <h1 className="h6 text-primary text-center">{t('welcome.title')}</h1>

          <div className="t-sm text-secondary text-center">
            <p>{t('welcome.redirectLine1')}</p>
            <p>{t('welcome.redirectLine2', { count: remainingSeconds })}</p>
          </div>

          <Button
            type="link"
            block
            className="flex items-center justify-center text-btn-secondary hover:text-highlight"
            onClick={redirectToPlatform}
          >
            {t('welcome.redirectNow')}
          </Button>
        </div>
      </div>

      <div className="max-w-[480px] text-center t-sm text-secondary">
        <Trans i18nKey="welcome.agreement">
          By registering on our platform means that you have read and agree to our
          <a className="text-primary" href="https://glows.ai/eula">
            EULA
          </a>
          ,
          <a className="text-primary" href="https://glows.ai/tos">
            Terms of Service
          </a>
          and
          <a className="text-primary" href="https://glows.ai/privacy">
            Privacy Policy
          </a>
          .
        </Trans>
      </div>
    </div>
  )
}
